/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import CommunicatorUtility from '../../communicator/utility/communicator-utility';
import ErrorName from '../../error/error-name';
import MediumType from '../../medium/medium-type';
import TransitionsViewerController from '../../transitions/transitions-viewer-controller';
import { BaseContentController } from './base-content-controller';

/**
 * @import { ContentController } from '.'
 * @import BrowserCapabilities from '../../capabilities/browser-capabilities'
 * @import CommunicatorSettings from '../../communicator/settings/communicator-settings'
 * @import { Medium } from '../../medium/medium';
 * @import ScheduleInfo from '../../schedule/schedule-info'
 * @import { ViewerType } from '../../viewer/type'
 * @import { Content } from '../content'
 * @import { ContentInfo } from '../info/content-info'
 */

/**
 * @implements {ContentController}
 */
export class MediumContentController extends BaseContentController {
  /**
   * @param {BrowserCapabilities} browserCapabilities
   * @param {Content} content
   * @param {ContentInfo} contentInfo
   * @param {HTMLElement} parentEl
   * @param {ScheduleInfo} scheduleInfo - TODO: používá se pouze property medium, volume. Ideálně nový typ.
   * @param {ViewerType | undefined} viewerType
   */
  constructor(
    browserCapabilities,
    content,
    contentInfo,
    parentEl,
    scheduleInfo,
    viewerType
  ) {
    const medium = content.medium;
    if (!medium) {
      throw new Error('content should be medium');
    }

    super();
    this.browserCapabilities = browserCapabilities;
    this.contentInfo = contentInfo;
    this.parentEl = parentEl;
    this.scheduleInfo = scheduleInfo;
    this.viewerType = viewerType;

    this.isPreloadable = isPreloadable(
      contentInfo.communicatorSettings,
      medium,
      viewerType
    );
    this.medium = medium;

    this.onError = this.onError.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
  }

  /**
   * Reakce na media.
   * @private
   * @param {Error} error Chybový objekt.
   * @returns {void}
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  /**
   * @private
   * @returns {void}
   */
  onPlayed() {
    this.emitter.emit('played', { medium: this.medium });
    this.emitter.emit('ended');
  }

  /**
   * @private
   * @returns {void}
   */
  onPlaying() {
    this.emitter.emit('playing', { medium: this.medium });
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      // Pokud se medium neumí prelodovat, tak preloadMedium
      // voláme až společně se start metodou.
      if (!this.isPreloadable) {
        return;
      }
      await this.preloadMedium();
    });
  }

  /**
   * @returns {Promise<void>}
   */
  async preloadMedium() {
    const { scheduleInfo, viewerType } = this;
    if (!viewerType) {
      return;
    }

    const sourceCreator = viewerType.getSourceCreator();
    const source = await sourceCreator.lend(this.parentEl);
    const el = source.getEl();
    const viewerFrame = source.getFrame();
    try {
      viewerFrame.seize(scheduleInfo);
      let controller = viewerType.createController(
        el,
        this.contentInfo.communicatorSettings
      );

      if (await this.browserCapabilities.transitions()) {
        controller = new TransitionsViewerController(
          this.contentInfo.communicatorSettings,
          el,
          controller
        );
      }

      controller.on('error', this.onError);
      controller.on('played', this.onPlayed);
      controller.on('playing', this.onPlaying);
      await controller.seize(scheduleInfo);

      this.controller = controller;
      this.source = source;
    } catch (error) {
      // Při chybě je žádoucí vypůjčený zdroj vrátit, aby nám nekompletní media nezabírala zdroje.
      sourceCreator.receive(source);
      viewerFrame.release();
      throw error;
    }
  }

  /** @type {ContentController['restart']} */
  async restart() {
    await super.restartCallback(async () => {
      const { controller, scheduleInfo, source, viewerType } = this;
      if (!controller || !source || !viewerType) {
        return;
      }
      await controller.leave();
      await controller.release();
      controller.destroy();
      controller.off('error', this.onError);
      controller.off('played', this.onPlayed);
      controller.off('playing', this.onPlaying);
      const el = source.getEl();
      // TODO: Možná by stačilo povolit přechod released ==> seized, aby
      // se controller nemusel vytvářet znovu.
      const newController = viewerType.createController(
        el,
        this.contentInfo.communicatorSettings
      );
      this.controller = newController;
      newController.on('error', this.onError);
      newController.on('played', this.onPlayed);
      newController.on('playing', this.onPlaying);
      await newController.seize(scheduleInfo);
      await newController.enter(0);
    });
  }

  /** @type {ContentController['start']} */
  async start(offsetMs) {
    await super.startCallback(() => this.startMedium(offsetMs));
  }

  /**
   * @private
   * @param {number} offsetMs
   */
  async startMedium(offsetMs) {
    const { viewerType } = this;
    if (!viewerType) {
      throw notSupportedMimeType(this.medium.name);
    }

    // Pokud nešlo medium přednačíst např. že bylo 4k nebo se při preloadu vyskytla
    // chyba, např. dosažení maxima vypůjčených zdrojů.
    if (!this.controller) {
      await this.preloadMedium();
    }
    if (!this.source) {
      throw new Error(`viewer source not created for ${this.medium.name}`);
    }
    if (!this.controller) {
      throw new Error(`viewer controller not created for ${this.medium.name}`);
    }

    if (offsetMs > 0) {
      if (!viewerType.isStatic(this.medium)) {
        if (!viewerType.isSeekable(this.medium) && offsetMs > 1000) {
          // Obsah není seekovatelný a zároveň začátek obsahu neodpovídá času spuštění.
          throw seekError();
        }
      }
    }
    const viewerFrame = this.source.getFrame();
    viewerFrame.enter();
    await this.controller.enter(offsetMs);
  }

  /** @type {ContentController['stop']} */
  async stop() {
    // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
    // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    await super.stopCallback(() => this.stopMedium().catch(() => {}));
  }

  /**
   * @private
   */
  async stopMedium() {
    const { viewerType } = this;
    if (!viewerType) {
      return;
    }

    const { controller, source } = this;
    if (controller) {
      try {
        await controller.leave();
        await controller.release();
        controller.destroy();
        controller.off('error', this.onError);
        controller.off('played', this.onPlayed);
        controller.off('playing', this.onPlaying);
      } catch {
        // Umyslené ignorování chyby, která mohla vzniknout při uvolňování.
        // Je žádoucí aby metoda pokračovala dále.
      }
      this.controller = undefined;
    }

    const viewerFrame = source?.getFrame();
    if (viewerFrame) {
      viewerFrame.leave();
      viewerFrame.release();
    }

    if (source) {
      const sourceCreator = viewerType.getSourceCreator();
      await sourceCreator?.receive(source);
    }
    this.source = undefined;
  }
}

/**
 * @param {string} name
 * @returns {Error}
 */
export function notSupportedMimeType(name) {
  const error = new Error(`unsupported MIME type ${name}`);
  error.name = ErrorName.UnsupportedMimeTypeError;
  return error;
}

export function seekError() {
  const error = new Error('seek not supported, start too delayed');
  error.name = ErrorName.SeekError;
  return error;
}

/**
 * @param {CommunicatorSettings} communicatorSettings
 * @param {Medium} medium
 * @param {ViewerType | undefined} viewerType
 * @returns {boolean}
 */
function isPreloadable(communicatorSettings, medium, viewerType) {
  if (!viewerType) {
    return false;
  }

  const matchedContentType = [
    { type: 'video', mime: 'video/*' },
    { type: 'web', mime: 'text/html' },
    { type: 'widget', mime: 'application/x-kiwi-web-1' }
  ].find(({ mime }) => new MediumType(medium.type).match(mime));

  if (matchedContentType) {
    const preloadDisable = communicatorSettings.get(
      `player.light.viewer.${matchedContentType.type}.preloadDisable`
    );
    const preloadDisableValue =
      CommunicatorUtility.parseBoolean(preloadDisable);

    // Explicitni true/false v konfiguraci ma prednost pred defaultni logikou vieweru.
    if (preloadDisableValue !== undefined) {
      return !preloadDisableValue;
    }
  }

  // V pripade neexplicitni konfigurace se vysledek urci dle typu media.
  return viewerType.isPreloadable(medium);
}
